// CORNER_V2: After a user drags a field on a TEMPLATE-LINKED document,
// surface a small banner asking whether to persist the move to the
// template (so future docs inherit the new position) or keep it
// local to this instance only.
//
// The prompt is dismissable, position is fixed bottom-center over the
// viewer, and it auto-replaces itself if the user drags ANOTHER field
// before deciding (the new field becomes the subject — older one keeps
// its instance-only override).
//
// Hidden entirely when no template is linked (mode='instance-only',
// the existing behaviour pre-template-memory).
import { useEffect, type CSSProperties } from 'react';
import { APP } from '../app/tokens';

export interface SaveToTemplatePromptProps {
  /** Human label of the field that was just moved (e.g. "Buyer signature"). */
  fieldLabel: string;
  /** Display name of the linked template (e.g. "Acme NDA"). */
  templateName: string;
  /** User chose "save to template too" — parent persists to template. */
  onSaveToTemplate: () => void | Promise<void>;
  /** User chose "this doc only" — dismiss without changing the template. */
  onKeepLocal: () => void;
  /** Auto-dismiss timeout in ms. 0 disables. Default: 0 (sticky). */
  autoDismissMs?: number;
}

export function SaveToTemplatePrompt({
  fieldLabel,
  templateName,
  onSaveToTemplate,
  onKeepLocal,
  autoDismissMs = 0,
}: SaveToTemplatePromptProps) {
  useEffect(() => {
    if (!autoDismissMs) return;
    const t = setTimeout(onKeepLocal, autoDismissMs);
    return () => clearTimeout(t);
  }, [autoDismissMs, onKeepLocal]);

  return (
    <div style={WRAPPER} role="status" aria-live="polite">
      <div style={CARD}>
        <div style={ICON}>📐</div>
        <div style={TEXT_COLUMN}>
          <div style={TITLE}>
            Moved <strong style={STRONG}>{fieldLabel}</strong>
          </div>
          <div style={SUB}>
            Save this position to the <strong style={STRONG}>{templateName}</strong> template
            so future docs use it too?
          </div>
        </div>
        <div style={ACTIONS}>
          <button
            type="button"
            onClick={onKeepLocal}
            style={SECONDARY_BTN}
            title="Keep this change on this document only"
          >
            Just this doc
          </button>
          <button
            type="button"
            onClick={onSaveToTemplate}
            style={PRIMARY_BTN}
            title={`Update the ${templateName} template`}
          >
            Save to template
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────

const WRAPPER: CSSProperties = {
  // Fixed-bottom center overlay above the viewer canvas. zIndex sits
  // above FieldMarker (~10) and the toolbar but below modals (9999).
  position: 'absolute',
  left: '50%',
  bottom: 24,
  transform: 'translateX(-50%)',
  zIndex: 200,
  pointerEvents: 'none',
};

const CARD: CSSProperties = {
  pointerEvents: 'auto',
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '12px 16px 12px 14px',
  borderRadius: 12,
  backgroundColor: 'var(--app-bg-card-body)',
  border: `1px solid ${APP.border}`,
  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.18)',
  fontFamily: APP.fontFamily,
  maxWidth: 560,
  // Slide-in animation so the prompt doesn't pop and startle.
  animation: 'cornerPromptSlideUp 220ms ease-out',
};

const ICON: CSSProperties = {
  fontSize: 22,
  flexShrink: 0,
};

const TEXT_COLUMN: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
};

const TITLE: CSSProperties = {
  fontSize: 13,
  color: APP.textPrimary,
  fontWeight: 500,
};

const STRONG: CSSProperties = {
  fontWeight: 700,
  color: APP.textPrimary,
};

const SUB: CSSProperties = {
  fontSize: 12,
  color: APP.textMuted,
  lineHeight: 1.4,
};

const ACTIONS: CSSProperties = {
  display: 'flex',
  gap: 6,
  flexShrink: 0,
};

const PRIMARY_BTN: CSSProperties = {
  padding: '7px 12px',
  borderRadius: 8,
  border: 'none',
  cursor: 'pointer',
  backgroundColor: APP.accentWorkflowPrimary,
  color: '#FFFFFF',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: APP.fontFamily,
};

const SECONDARY_BTN: CSSProperties = {
  padding: '7px 12px',
  borderRadius: 8,
  border: `1px solid ${APP.border}`,
  cursor: 'pointer',
  backgroundColor: 'transparent',
  color: APP.textPrimary,
  fontSize: 12,
  fontWeight: 500,
  fontFamily: APP.fontFamily,
};
